import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsApi } from '../services/api';
import { TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';

const LOGO_URL = 'https://skiverse.skicorp.in/upload/process/79871716008647color_logo_without_tagline.png';

// Format currency in Crores
const formatCrores = (amount) => {
  if (!amount) return '₹0';
  const crores = amount / 10000000;
  if (crores >= 1) {
    return `₹${crores.toFixed(2)} Cr`;
  }
  const lakhs = amount / 100000;
  return `₹${lakhs.toFixed(2)} L`;
};

export const Layout = ({ children, customerInfo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBudgetPage = location.pathname.includes('/budget/');
  const isAuditPage = location.pathname.includes('/audit');
  
  // Global stats for admin
  const [globalStats, setGlobalStats] = useState(null);
  
  useEffect(() => {
    const fetchGlobalStats = async () => {
      if (user?.role === 'admin') {
        try {
          const stats = await statsApi.getGlobalStats();
          setGlobalStats(stats);
        } catch (err) {
          console.error('Failed to fetch global stats:', err);
        }
      }
    };
    fetchGlobalStats();
  }, [user]);

  // Get user initials
  const getUserInitials = () => {
    if (!user?.name) return '??';
    return user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };

  // Get first name
  const getFirstName = () => {
    if (!user?.name) return 'Guest';
    return user.name.split(' ')[0];
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="layout-container">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40" data-testid="header">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-9 w-auto"
              data-testid="company-logo"
            />
            <div className="h-6 w-px bg-gray-200" />
            {!isBudgetPage && (
              <div>
                <span className="font-heading text-lg font-semibold text-gray-900">
                  Customer Budget Planning
                </span>
                <p className="text-xs text-gray-500">
                  Select a customer to manage their yearly product budget
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Global Stats for Admin */}
            {user?.role === 'admin' && globalStats && (
              <div className="hidden md:flex items-center gap-3 mr-4 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200" data-testid="global-stats">
                {(globalStats.ly_budget_total || 0) > 0 && (
                  <>
                    <div className="text-center">
                      <p className="text-[9px] text-gray-400 uppercase">LY Budget</p>
                      <p className="text-xs font-semibold text-gray-600">{formatCrores(globalStats.ly_budget_total)}</p>
                    </div>
                    <div className="h-7 w-px bg-gray-200" />
                  </>
                )}
                <div className="text-center">
                  <p className="text-[9px] text-gray-400 uppercase">LY Sale</p>
                  <p className="text-xs font-semibold text-gray-600">{formatCrores(globalStats.total_ly_budget)}</p>
                </div>
                <div className="h-7 w-px bg-gray-200" />
                <div className="text-center">
                  <p className="text-[9px] text-gray-400 uppercase">TY Budget</p>
                  <p className="text-xs font-semibold text-blue-600">{formatCrores(globalStats.total_ty_budget)}</p>
                </div>
                <div className="h-7 w-px bg-gray-200" />
                {(globalStats.ly_budget_total || 0) > 0 && (
                  <>
                    <div className="text-center" title="LY Budget vs TY Budget">
                      <p className="text-[9px] text-gray-400 uppercase">Bgt %</p>
                      <div className={`flex items-center justify-center gap-0.5 text-xs font-semibold ${
                        (globalStats.ly_budget_vs_ty_pct || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}>
                        {(globalStats.ly_budget_vs_ty_pct || 0) >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {(globalStats.ly_budget_vs_ty_pct || 0).toFixed(1)}%
                      </div>
                    </div>
                    <div className="h-7 w-px bg-gray-200" />
                  </>
                )}
                <div className="text-center" title="LY Sale vs TY Budget">
                  <p className="text-[9px] text-gray-400 uppercase">Sale %</p>
                  <div className={`flex items-center justify-center gap-0.5 text-xs font-semibold ${
                    globalStats.diff > 0 ? 'text-green-600' : globalStats.diff < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {globalStats.diff > 0 ? <TrendingUp className="h-3 w-3" /> : 
                     globalStats.diff < 0 ? <TrendingDown className="h-3 w-3" /> : 
                     <Minus className="h-3 w-3" />}
                    {globalStats.diff_percentage?.toFixed(1)}%
                  </div>
                </div>
              </div>
            )}
            {/* Audit Log Link */}
            {user && (
              <button
                onClick={() => navigate('/audit')}
                className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isAuditPage ? 'bg-zinc-900 text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                data-testid="audit-log-nav"
              >
                <Clock className="h-3.5 w-3.5" />
                Audit Log
              </button>
            )}
            {user && (
              <span className="text-sm text-gray-600 hidden sm:block">
                Welcome, <span className="font-medium text-gray-900">{getFirstName()}</span>!
              </span>
            )}
            <div className="h-8 w-8 rounded-full bg-zinc-900 flex items-center justify-center" title={user?.name || 'Not logged in'}>
              <span className="text-xs font-medium text-white">{getUserInitials()}</span>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};

export default Layout;
